import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NavbarChevronIcon from '../layout/NavbarChevronIcon';
import DetailNavbarLeft from '../shared/DetailNavbarLeft';
import NavbarCloseButton from '../shared/NavbarCloseButton';
import { findSiblingIndex } from '../orders/orderdetail/orderDetailNavState';
import {
  DEFAULT_TASK_DETAIL_FROM_PATH,
  parseTaskDetailNavState,
} from './taskDetailNavState';
import '../orders/orderdetail/pick-list.css';

interface TaskDetailNavbarProps {
  readonly title?: string;
  readonly subtitle?: string;
  readonly showSiblingNav?: boolean;
  /** When false, center title area is omitted (detail body carries title/status). */
  readonly showTitle?: boolean;
}

function TaskDetailNavbar({
  title,
  subtitle,
  showSiblingNav = true,
  showTitle = true,
}: TaskDetailNavbarProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: taskId } = useParams<{ id: string }>();
  const navState = parseTaskDetailNavState(location.state);

  const siblingIndex = findSiblingIndex(navState.siblingTaskIds, taskId);
  const hasSiblings = showSiblingNav && navState.siblingTaskIds.length > 1 && siblingIndex >= 0;
  const prevSiblingId =
    hasSiblings && siblingIndex > 0 ? navState.siblingTaskIds[siblingIndex - 1] : null;
  const nextSiblingId =
    hasSiblings && siblingIndex < navState.siblingTaskIds.length - 1
      ? navState.siblingTaskIds[siblingIndex + 1]
      : null;

  const handleClose = (): void => {
    navigate(navState.fromPath || DEFAULT_TASK_DETAIL_FROM_PATH);
  };

  const goToSibling = (siblingId: string): void => {
    navigate(`/tasks/${siblingId}`, { replace: true, state: navState });
  };

  return (
    <nav className="order-detail-navbar" aria-label="Task details">
      <DetailNavbarLeft />
      {showTitle && title ? (
        <div className="order-detail-navbar-titles">
          <h1 className="order-detail-navbar-order-number">{title}</h1>
          {subtitle ? (
            <div className="order-detail-navbar-order-name">{subtitle}</div>
          ) : null}
        </div>
      ) : (
        <div className="order-detail-navbar-titles" aria-hidden="true" />
      )}
      <div className="order-detail-navbar-right">
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => prevSiblingId != null && goToSibling(prevSiblingId)}
          disabled={prevSiblingId == null}
          aria-label="Previous task"
        >
          <NavbarChevronIcon direction="left" />
        </button>
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => nextSiblingId != null && goToSibling(nextSiblingId)}
          disabled={nextSiblingId == null}
          aria-label="Next task"
        >
          <NavbarChevronIcon direction="right" />
        </button>
        <NavbarCloseButton onClose={handleClose} />
      </div>
    </nav>
  );
}

export default TaskDetailNavbar;
